import { createFileRoute, Outlet } from '@tanstack/react-router';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { type TabItem, TabsBar } from '@/common/TabsBar';
import { useCurrentUser } from '~/hooks/useCurrentUser';

/**
 * The coordinator's and partner's follow-up portal, mounted at `/follow-ups/*`
 * and scoped to the caller, since the follow-up groups legacy hung these tabs
 * on are gone.
 *
 * No profile-type gate on the shell: it's the parent of the follow-up detail,
 * whose server gate also admits the student, their study helper and admins, all
 * of whom reach it from a View action. The detail's gate is its endpoint's 404.
 *
 * The subnav is gated separately, so a study helper viewing a detail doesn't
 * see two tabs that both refuse them.
 */
function useFollowUpTabs(): TabItem[] {
  const { t } = useTranslation();
  return [
    { title: t('wbs.pending-requests'), to: 'pending-requests' },
    { title: t('wbs.follow-ups'), to: 'entries' },
  ];
}

function FollowUpsLayout(): ReactElement {
  const tabs = useFollowUpTabs();
  const { me, isPending } = useCurrentUser();

  // `me.profile_type`, not `useCurrentUser().isStudyHelper`, which includes
  // every non-student type, and not `useIsCoordinator`, which needs a group
  // slug and never resolves on a top-level route.
  const hasSubnav =
    !isPending &&
    (me?.profile_type === 'FOLLOWUP_COORDINATOR' ||
      me?.profile_type === 'FOLLOWUP_PARTNER');

  return (
    <>
      {hasSubnav && <TabsBar tabs={tabs} base="/follow-ups" />}
      <Outlet />
    </>
  );
}

export const Route = createFileRoute('/_app/follow-ups')({
  component: FollowUpsLayout,
});
